import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import HelpButton from 'help/HelpButton';
import deepCopy from 'lib/deepCopy';
import {PipelineConfigJsonSpec} from 'pipelines/StepShapeUtils';
import {FONT_PROXIMA_13, FONT_WEIGHT_PROXIMA_SEMIBOLD} from 'styles/FontConstants';
import {GRAY_DARKER, RED} from 'ui-library/Colors';
import {FloatingInteraction, FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';

// eslint-disable-next-line import-access/jsdoc -- ShapeEditor is package-private but ui-schemas is part of the same package
import ShapeEditor from '../editors/ShapeEditor';
import {
  getConfigShapeAtConfigShapePath,
  maybeGetValueFromConfig,
} from '../step-config-settings/StepConfigSettingsUtils';
import {StepTypeConfigUISchemaV2} from '../step-config-settings/StepConfigUISchemas';
import {
  BACKEND_FIELD_GENERATOR_CONFIG,
  CustomComponentEditorProps,
  CUSTOM_COMPONENT_KEY,
  FIELD_ORDER_UI_SCHEMA_KEY,
  NUM_COLUMNS_KEY,
} from '../StepConfigEditorTypes';

interface PortMeta {
  name: string;
  description: string;
  isOptional: boolean;
  type: string;
}

// Read a backend-provided `disabledMessage` off a hydrated shape (e.g. "No ports for this protocol"),
// if present.
function getDisabledMessage(shape: PipelineConfigJsonSpec): string | undefined {
  const message = (shape as Record<string, unknown>).disabledMessage;
  return typeof message === 'string' && message !== '' ? message : undefined;
}

// Inline help text shown in place of an editor when the backend reports there is nothing to
// configure (e.g. no protocol selected, or the protocol has no ports / parameters).
function EmptySectionMessage({message}: {message: string}): JSX.Element {
  return <div className={css(portStyles.emptyMessage)}>{message}</div>;
}

function PortSectionHeader({meta, fallbackName}: {meta?: PortMeta; fallbackName: string}): JSX.Element {
  const name = meta?.name ?? fallbackName;
  const isRequired = meta != null && !meta.isOptional;
  // Only surface a description tooltip when it adds information beyond the port name.
  const description = meta?.description && meta.description !== name ? meta.description : undefined;
  return (
    <div className={css(portStyles.sectionHeader)}>
      {name}
      {isRequired && <span className={css(portStyles.requiredAsterisk)}>&nbsp;*</span>}
      {description != null && (
        <HelpButton
          renderContent={() => <span className="u-pre-wrap u-normal-font-weight">{description}</span>}
          iconClassName="u-mini-spacing-left"
          placement={FloatingPlacement.TOP}
          interaction={FloatingInteraction.HOVER}
        />
      )}
    </div>
  );
}

// A port's labware/plate fields live on the shared `ports` elementSpec, so they render as required
// for every row. Clone the config shape and relax those fields to optional for an optional port.
// Exported for testing.
export function relaxPortFieldsRequired(configShape: PipelineConfigJsonSpec): PipelineConfigJsonSpec {
  const cloned = deepCopy(configShape);
  if (cloned.kind !== 'shape') {
    return cloned;
  }
  const portsShape = cloned.fields.ports;
  if (portsShape?.kind !== 'array' || portsShape.elementSpec.kind !== 'shape') {
    return cloned;
  }
  const fields = portsShape.elementSpec.fields;
  for (const fieldName of ['labwareTypeId', 'plateIdColumn']) {
    if (fields[fieldName] != null) {
      fields[fieldName] = {...fields[fieldName], required: false};
    }
  }
  return cloned;
}

const portStyles = StyleSheet.create({
  // Show the port name with the casing returned by the HighRes API (no uppercasing), bolded so it
  // reads as a clear section header.
  sectionHeader: {
    ...FONT_PROXIMA_13,
    color: GRAY_DARKER,
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
    display: 'flex',
    alignItems: 'center',
    marginBottom: 8,
  },
  // Add vertical separation between consecutive port sections.
  portSection: {
    ':not(:first-child)': {
      marginTop: 10,
    },
  },
  requiredAsterisk: {
    color: RED,
  },
  emptyMessage: {
    ...FONT_PROXIMA_13,
    color: GRAY_DARKER,
  },
});

// Exported for testing.
export function HighresPortsEditor({
  config,
  configShape,
  onChangeConfig,
  disabled,
}: CustomComponentEditorProps): JSX.Element {
  const portsShape = getConfigShapeAtConfigShapePath(['ports'], configShape, config);
  if (portsShape.kind !== 'array') {
    return <></>;
  }

  const portMeta = (portsShape as Record<string, unknown>).__portMeta as Record<string, PortMeta> | undefined;
  const ports = maybeGetValueFromConfig(config, ['ports']);
  if (!Array.isArray(ports) || ports.length === 0) {
    // Surface the backend's reason (e.g. "Select a Scientific Protocol first" / "No ports for this
    // protocol") instead of rendering a blank section.
    const portIdShape =
      portsShape.elementSpec.kind === 'shape' ? portsShape.elementSpec.fields.portId : undefined;
    const message = portIdShape != null ? getDisabledMessage(portIdShape) : undefined;
    return message != null ? <EmptySectionMessage message={message} /> : <></>;
  }

  // The ports elementSpec is shared across all rows, so a port's labware/plate fields would
  // otherwise always render as required. Compute one relaxed clone (lazily, only if any port is
  // optional) and reuse it across rows rather than deep-copying the shape per optional port.
  const hasOptionalPort = Object.values(portMeta ?? {}).some((meta) => meta.isOptional);
  const relaxedConfigShape = hasOptionalPort ? relaxPortFieldsRequired(configShape) : configShape;

  return (
    <>
      {ports.map((port, index) => {
        const portId =
          port != null && typeof port === 'object' && !Array.isArray(port)
            ? String((port as Record<string, unknown>).portId ?? '')
            : '';
        const meta = portMeta?.[portId];
        const portConfigShape = meta?.isOptional ? relaxedConfigShape : configShape;
        return (
          <div key={portId || index} className={css(portStyles.portSection)}>
            <PortSectionHeader meta={meta} fallbackName={`Port ${index + 1}`} />
            <ShapeEditor
              configShape={portConfigShape}
              config={config}
              onChangeConfig={onChangeConfig}
              disabled={disabled}
              uiSchema={{
                [NUM_COLUMNS_KEY]: 2,
                [FIELD_ORDER_UI_SCHEMA_KEY]: [`ports_${index}_labwareTypeId`, `ports_${index}_plateIdColumn`],
                [`ports_${index}_labwareTypeId`]: {
                  displayName: 'Labware Type',
                  infoText: 'The type of labware (plate format) used for this port.',
                  configShapePath: ['ports', index, 'labwareTypeId'],
                },
                [`ports_${index}_plateIdColumn`]: {
                  displayName: 'Plate Column',
                  infoText: 'The column in the input dataset that contains the plate barcodes for this port.',
                  configShapePath: ['ports', index, 'plateIdColumn'],
                },
              }}
            />
          </div>
        );
      })}
    </>
  );
}

// Exported for testing.
export function HighresParametersEditor({
  config,
  configShape,
  onChangeConfig,
  disabled,
}: CustomComponentEditorProps): JSX.Element {
  const parametersShape = getConfigShapeAtConfigShapePath(['parameters'], configShape, config);

  if (parametersShape.kind !== 'shape') {
    return <></>;
  }

  const shapeWithMeta = parametersShape as Record<string, unknown>;
  const fieldOrder =
    (shapeWithMeta.__fieldOrder as Array<string> | undefined) ?? Object.keys(parametersShape.fields);
  const displayNames = (shapeWithMeta.__displayNames as Record<string, string> | undefined) ?? {};

  if (fieldOrder.length === 0) {
    // Surface the backend's reason (e.g. "No parameters for this protocol") instead of rendering
    // a blank section.
    const message = getDisabledMessage(parametersShape);
    return message != null ? <EmptySectionMessage message={message} /> : <></>;
  }

  return (
    <ShapeEditor
      configShape={configShape}
      config={config}
      onChangeConfig={onChangeConfig}
      disabled={disabled}
      uiSchema={{
        [FIELD_ORDER_UI_SCHEMA_KEY]: fieldOrder.map((name) => `parameters_${name}`),
        ...Object.fromEntries(
          fieldOrder.map((name) => [
            `parameters_${name}`,
            {
              displayName: displayNames[name] ?? name,
              configShapePath: ['parameters', name],
            },
          ])
        ),
      }}
    />
  );
}

export const RUN_HIGHRES_PROTOCOL_V0_UI_SCHEMA: StepTypeConfigUISchemaV2<
  PipelineStepType.RUN_HIGHRES_PROTOCOL,
  0
> = {
  [FIELD_ORDER_UI_SCHEMA_KEY]: [
    'scientificProtocolId',
    'name',
    'requestedByUserId',
    'cellarioSystemId',
    'ports',
    'parameters',
  ],
  [BACKEND_FIELD_GENERATOR_CONFIG]: {triggerFields: ['scientificProtocolId']},
  scientificProtocolId: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['scientificProtocolId'],
    scientificProtocolId: {
      displayName: 'Scientific Protocol',
      infoText:
        'The HighRes scientific protocol to execute. This determines the available ports and Cellario systems.',
      configShapePath: ['scientificProtocolId'],
    },
  },
  name: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['name'],
    name: {
      displayName: 'Name',
      infoText: 'A name for the work order that will be created in HighRes.',
      configShapePath: ['name'],
    },
  },
  requestedByUserId: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['requestedByUserId'],
    requestedByUserId: {
      displayName: 'Requested by',
      infoText: 'The HighRes user who is requesting this work order.',
      configShapePath: ['requestedByUserId'],
    },
  },
  cellarioSystemId: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['cellarioSystemId'],
    cellarioSystemId: {
      displayName: 'Cellario System',
      infoText:
        'The Cellario system that will execute the protocol. Available systems depend on the selected scientific protocol.',
      configShapePath: ['cellarioSystemId'],
    },
  },
  ports: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['ports'],
    ports: {
      displayName: 'Ports',
      infoText:
        'The ports of the protocol. Each port maps plate barcodes to a labware type. The ports are defined by the selected scientific protocol.',
      [CUSTOM_COMPONENT_KEY]: HighresPortsEditor,
    },
  },
  parameters: {
    [FIELD_ORDER_UI_SCHEMA_KEY]: ['parameters'],
    parameters: {
      displayName: 'Parameters',
      infoText:
        'The parameters of the protocol. Protocol parameters are defined by the selected scientific protocol.',
      [CUSTOM_COMPONENT_KEY]: HighresParametersEditor,
    },
  },
};
